import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { useTaskInbox } from "~/common/tasks/inbox";
import { useAccess } from "~/core/access";
import { api } from "~/core/api";
import { useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section, Stat } from "~/core/ui/page";
import { requestJson } from "~/modules/agent/lib/api/client/request";
import { listLogsContract } from "~/modules/agent/lib/api/contracts/logs";
import { listWorkspaceSchedulesContract } from "~/modules/agent/lib/api/contracts/schedules";

const refresh = { ...read, placeholderData: undefined, refetchInterval: 10_000, refetchOnWindowFocus: true };

export function TaskSummary({ orgId, brandId }: { orgId: string; brandId: string }) {
	const t = useT();
	const { agent } = useAccess();
	const base = `/w/${brandId}/tasks`;
	const inbox = useTaskInbox(orgId);
	const filter = { entity: brandId, days: 0, status: "running" };
	const loop = useQuery({
		queryKey: [...keys.org(orgId), "tasks", filter],
		queryFn: () => api.tasks(orgId, filter),
		...refresh,
	});
	const runs = useQuery({
		queryKey: [...keys.entity(brandId), "task-summary", "runs"],
		queryFn: ({ signal }) =>
			requestJson(listLogsContract, {
				query: { workspaceId: brandId, level: "running", includeTotal: true, limit: 1 },
				signal,
			}),
		enabled: agent,
		...refresh,
	});
	const schedules = useQuery({
		queryKey: [...keys.entity(brandId), "task-summary", "schedules"],
		queryFn: ({ signal }) =>
			requestJson(listWorkspaceSchedulesContract, {
				query: { workspaceId: brandId },
				signal,
			}),
		enabled: agent,
		...refresh,
	});
	function count(value: number | undefined, error: boolean, to: string, label: string) {
		return (
			<Link
				to={to}
				className="task-summary-count"
				aria-label={`${label}: ${error ? t("state.loadError") : (value ?? t("state.loading"))}`}
			>
				{error || value === undefined ? "—" : value}
			</Link>
		);
	}
	const loopRunning = `${base}/loop?status=running&days=0`;
	const loopQueued = `${base}/loop?status=queued&days=0`;
	return (
		<Section title={t("tasks.title")} lede={t("tasks.summaryHelp")}>
			<div className="overview-stats task-summary">
				<Stat
					label={t("tasks.inbox")}
					value={count(
						inbox.isPlaceholderData ? undefined : inbox.data?.items.length,
						inbox.isError,
						`${base}/inbox`,
						t("tasks.inbox"),
					)}
					foot={inbox.isError ? t("state.loadError") : t("tasks.needsAttention")}
				/>
				<Stat
					label={t("tasks.tabLoop")}
					value={count(
						loop.data?.counts.running,
						loop.isError,
						loopRunning,
						`${t("tasks.tabLoop")} · ${t("tasks.running")}`,
					)}
					foot={
						loop.isError ? (
							t("state.loadError")
						) : (
							<>
								{t("tasks.running")} ·{" "}
								<Link to={loopQueued} className="task-summary-count">
									{t("tasks.queued")} {loop.data?.counts.queued ?? "—"}
								</Link>
							</>
						)
					}
				/>
				{agent && (
					<Stat
						label={t("tasks.tabAgent")}
						value={count(
							runs.data?.total,
							runs.isError,
							`${base}/agent?level=running`,
							`${t("tasks.tabAgent")} · ${t("tasks.running")}`,
						)}
						foot={runs.isError ? t("state.loadError") : t("tasks.running")}
					/>
				)}
				{agent && (
					<Stat
						label={t("tasks.tabScheduled")}
						value={count(
							schedules.data?.schedules.filter((item) => item.status === "active" && !item.archivedAt).length,
							schedules.isError,
							`${base}/scheduled`,
							t("tasks.tabScheduled"),
						)}
						foot={schedules.isError ? t("state.loadError") : t("tasks.enabledSchedules")}
					/>
				)}
			</div>
		</Section>
	);
}
